import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface FineTuningJob {
  id: string;
  client_id: string;
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  base_model: string;
  fine_tuned_model: string | null;
  training_examples_count: number;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  created_at: string;
}

export interface ModelRegistry {
  current_model: string | null;
  base_model: string;
  last_trained_at: string | null;
  training_examples_count: number;
  status: 'active' | 'training' | 'inactive' | 'error';
  performance_metrics: any;
}

export interface PendingApprovals {
  pending_count: number;
  min_examples: number;
  can_train: boolean;
  needed: number;
}

class FineTuningService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  private async _fetch<T>(
    url: string,
    method: string,
    body?: any
  ): Promise<T> {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    const response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async getJobs(clientId: string): Promise<FineTuningJob[]> {
    const response = await this._fetch<{ jobs: FineTuningJob[] }>(
      `${this.baseUrl}/api/fine-tuning/${clientId}/jobs`,
      'GET'
    );
    return response.jobs;
  }

  async getModelRegistry(clientId: string): Promise<ModelRegistry> {
    return this._fetch<ModelRegistry>(
      `${this.baseUrl}/api/fine-tuning/${clientId}/model-registry`,
      'GET'
    );
  }

  async triggerRetraining(clientId: string): Promise<{ status: string; message: string }> {
    return this._fetch<{ status: string; message: string }>(
      `${this.baseUrl}/api/fine-tuning/${clientId}/retrain`,
      'POST'
    );
  }

  async getPendingApprovals(clientId: string): Promise<PendingApprovals> {
    return this._fetch<PendingApprovals>(
      `${this.baseUrl}/api/fine-tuning/${clientId}/pending-approvals`,
      'GET'
    );
  }
}

export const fineTuningService = new FineTuningService();
export default fineTuningService;

